import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMap } from 'react-map-gl/mapbox';
import { ButtonUI, LoadingSkeleton, TaptapList, TextWithTooltip } from 'sonata-react-components';

import { useListGrouping } from '../../../grouping/hooks/useListGrouping';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { ModifiedScreen } from '../../MapView/map.types';
import { useListFilterPanel } from '../Filters/useListFilterPanel';
import ListsHeader from '../ListsHeader/ListsHeader';

import type { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';
import type { GroupView } from '../../../grouping/domain/grouping.types';

import '../lists.scss';

import { useModals } from '@/router';
import { AttributionAndGroup } from '../common/AttributionAndGroup';
import { matchesScreenSearch } from './screensList.helpers';
import { useScreensFilters } from './useScreensFilters';

type Props = {
  mapId: string;
};

type State = {
  searchText: string[];
};

type TaptapListItem = {
  id: string;
  groupIds: string[];
  groups: GroupView[];
  searchText: string;
  subtitle: string;
  title: string;
  status: 'default' | 'highlighted' | 'notVisible';
  screen: Partial<ModifiedScreen>;
};

function ScreensList({ mapId }: Props) {
  const { formatMessage } = useIntl();
  const { [mapId]: map } = useMap();
  const {
    loadingScreensList,
    selectedItemId,
    selectedItems: { screens: selectedIds },
    setSelectedItemId,
    setSelectedItems,
    temporaryScreens
  } = useMultiFlightMap();
  const modals = useModals();

  const screensList = useMemo(() => temporaryScreens.filter(shape => !shape.deleted), [temporaryScreens]);

  const {
    clearGroupFilter,
    deleteItems,
    editGroup,
    groupFilter,
    groupsOf,
    hasActiveGroupFilter,
    isHighlighted,
    matchesGroupFilter
  } = useListGrouping('screens');

  const {
    activeCount: activeScreenFiltersCount,
    clear: clearScreenFilters,
    filters: screenFilters,
    hasActive: hasActiveScreenFilters,
    matches: matchesScreenFilters
  } = useScreensFilters(screensList);

  const { matches: matchesLocation, panel: filterPanel } = useListFilterPanel('screens', screensList, {
    activeCount: activeScreenFiltersCount + (hasActiveGroupFilter ? 1 : 0),
    filters: [...screenFilters, groupFilter],
    hasActive: hasActiveScreenFilters || hasActiveGroupFilter,
    onClear: () => {
      clearScreenFilters();
      clearGroupFilter();
    }
  });

  const [{ searchText }, setState] = useState<State>({
    searchText: []
  });

  const handleSearchChange = (text: string) => {
    const searchValues = text.length ? text.split(',').map((a: string) => a.trim()) : [];
    setState(prevState => ({
      ...prevState,
      searchText: searchValues
    }));
  };

  const handleDeleteChange = (screenSourceElementId: number) => {
    deleteItems([String(screenSourceElementId)]);
  };

  const handleDeleteAllChange = () => {
    deleteItems(selectedIds);
  };

  const handleCenterMapOnPoint = (screen: GeoDataAndScreensInteface) => {
    if (screen?.source_element_id && map) {
      setSelectedItemId(String(screen.source_element_id));
      map.flyTo({
        center: [screen.longitude!, screen.latitude!],
        curve: 0.8,
        speed: 3,
        zoom: 16
      });
    }
  };

  const data: TaptapListItem[] = useMemo(() => {
    if (!screensList[0]) {
      return [];
    }
    return screensList.map(screen => ({
      groupIds: screen?.groups?.map(String) ?? [],
      groups: groupsOf(screen?.groups),
      id: String(screen.source_element_id),
      screen,
      searchText: [screen.device_id, screen.adExchange_str].filter(Boolean).join(' | '),
      status:
        selectedItemId === String(screen.source_element_id) || isHighlighted(String(screen.source_element_id))
          ? 'highlighted'
          : 'default',
      subtitle: screen.address || 'N/A',
      title: screen.name || 'N/A'
    }));
  }, [screensList, selectedItemId, groupsOf, isHighlighted]);

  const formattedData = useMemo(() => {
    const filteredBySearch = (() => {
      if (searchText.length === 0) {
        return data;
      }
      return data.filter(item => matchesScreenSearch(item, searchText));
    })();

    return filteredBySearch.filter(
      item => matchesGroupFilter(item.groupIds) && matchesScreenFilters(item.screen) && matchesLocation(item.screen)
    );
  }, [data, searchText, matchesGroupFilter, matchesScreenFilters, matchesLocation]);

  const defaultSelectedItems = useMemo(
    () => formattedData.filter(item => selectedIds.includes(item.id)),
    [formattedData, selectedIds]
  );

  const handleSelectionChange = (selectedRows: { id: string }[]) => {
    setSelectedItems(
      'screens',
      selectedRows.map(row => row.id)
    );
  };

  const handleShowAddScreensModal = () => {
    modals.open('/campaigns/new/poiFinderScreensModal', {
      state: { modalId: 'poi-finder-screens-modal' }
    });
  };

  if (loadingScreensList) {
    return <LoadingSkeleton count={10} height={62} />;
  }

  return (
    <div className="mapList">
      <ListsHeader
        actionButtonsId={`actionButtons-screensList-${mapId}`}
        filtersId={`filters-screensList-${mapId}`}
        panel={filterPanel}
        search={{
          id: `screensList-textFilter-${mapId}`,
          name: 'screensList-textFilter',
          onChange: handleSearchChange,
          placeholder: formatMessage({
            defaultMessage: 'Search by name, ID...',
            id: 'multiFlightListsMap.search.screens'
          })
        }}
      />
      <div className="mapList__listContainer">
        <TaptapList
          className="mapList__list"
          data={formattedData}
          defaultSelectedItems={defaultSelectedItems}
          id={`screensList-${mapId}`}
          onSelectionChange={items => handleSelectionChange(items as { id: string }[])}
          selectionType="rowClick"
        >
          {screensList.length > 0 && (
            <TaptapList.Toolbar>
              <TaptapList.Toolbar.SelectionSummary />
              <TaptapList.Toolbar.SelectAll />
              <div className="toolbarDeleteAll">
                <ButtonUI
                  disabled={selectedIds.length === 0}
                  icon="fa-regular fa-trash"
                  id={`deleteSelectedAction-${mapId}`}
                  onClick={handleDeleteAllChange}
                  shape="link"
                  size="ultraSmall"
                  text={formatMessage({
                    defaultMessage: 'Delete',
                    id: 'multiFlightListsMap.emptyState.screens.delete'
                  })}
                  type="secondary"
                />
              </div>
            </TaptapList.Toolbar>
          )}
          <TaptapList.RowsListVirtualized<TaptapListItem>
            emptyStateDescription={formatMessage(
              screensList.length
                ? {
                    defaultMessage: 'No screens match the filters',
                    id: 'multiFlightListsMap.emptyState.screens.noMatches'
                  }
                : {
                    defaultMessage: 'Add your first Screen clicking below.',
                    id: 'multiFlightListsMap.emptyState.screens.getStarted'
                  }
            )}
            emptyStateIcon="fa-solid fa-empty-set"
            emptyStateTitle={formatMessage(
              screensList.length
                ? { defaultMessage: 'No screens', id: 'multiFlightListsMap.emptyState.screens.title' }
                : { defaultMessage: 'Ready to get started?', id: 'multiFlightListsMap.emptyState.readyToStart' }
            )}
            focusItemId={selectedItemId}
          >
            {listItem =>
              listItem.map(item => (
                <TaptapList.RowsListVirtualized.Row id={item.id} key={item.id} virtualData={item.virtualData}>
                  <div className="mapList__listRowsListRowItemTitle">
                    <TextWithTooltip text={item.title} />
                  </div>
                  <div className="mapList__listRowsListRowItemActions">
                    <ButtonUI
                      className="mapList__listRowsListRowDeleteButton"
                      icon="fa solid fa-xmark"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleDeleteChange(item.screen.source_element_id!);
                      }}
                      shape="link"
                      size="ultraSmall"
                      type="secondary"
                    />
                  </div>
                  <div className="mapList__listRowsListRowItemSubtitle">
                    <ButtonUI
                      className="mapList__listRowsListRowPinButton"
                      icon="fa-solid fa-map-pin"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleCenterMapOnPoint(item.screen as GeoDataAndScreensInteface);
                      }}
                      shape="link"
                      size="ultraSmall"
                      type="secondary"
                    />
                    <TextWithTooltip text={item.subtitle} />
                  </div>
                  <AttributionAndGroup groupMenu={{ family: 'screens', groups: item.groups, onEditGroup: editGroup }} />
                </TaptapList.RowsListVirtualized.Row>
              ))
            }
          </TaptapList.RowsListVirtualized>
        </TaptapList>
      </div>
      <div className="mapList__addScreenContainer">
        <ButtonUI
          className="mapList__addScreenButton"
          icon="fa-solid fa-plus"
          onClick={handleShowAddScreensModal}
          text={formatMessage({
            defaultMessage: 'Add Screens',
            id: 'multiFlightListsMap.screens.addButton'
          })}
          type="secondary"
        />
      </div>
    </div>
  );
}
export default ScreensList;
